// Shared plumbing for the ODA metering points the backend proxies from the
// external NrgOda API. The DTOs and the search read live here — not inside
// `modules/oda` — because the global search palette (`modules/topbar`) needs
// them too, and cross-module imports are banned (see AGENTS.md).

import baseApi from './client';
import { ODA_API } from './endpoints';

// Shapes mirror the external NrgOda API DTOs verbatim — the backend relays its
// responses unchanged through `/api/oda/*` (see NrgOda.Shared/Models).

export interface MeteringPoint {
  id: number;
  eanId: string;
  meterId: string | null;
  locationName: string | null;
  latestQueryDate: string | null;
  totalMessages: number;
}

export interface MeteringPointsResponse {
  items: MeteringPoint[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** Suggestions the palette asks for — one short page, never the whole set. */
const SEARCH_PAGE_SIZE = 8;

/**
 * Query options for the metering points matching `search`, as suggestions.
 *
 * The ODA API already matches EAN / meter id / location name server-side, so the
 * palette reads only the first page rather than draining every page the way the
 * ODA table does (`useMeteringPoints` — it needs the whole set for its group
 * chips). Distinct cache key, so the two never overwrite each other.
 */
export function meteringPointSearchQueryOptions(search: string) {
  return {
    queryKey: ['oda', 'metering-points', 'search', search] as const,
    queryFn: async (): Promise<MeteringPoint[]> => {
      const params = new URLSearchParams({
        page: '1',
        pageSize: String(SEARCH_PAGE_SIZE),
        search,
      });
      const { data } = await baseApi.get<MeteringPointsResponse>(
        `${ODA_API.METERING_POINTS}?${params}`,
      );
      return data.items;
    },
    staleTime: 60_000,
  } as const;
}
